<template>
  <div class="daily-performance__row" :class="expanded ? 'expanded' : ''">
    <div class="daily-performance__row--top">
      <v-btn
        icon
        small
        @click="expand()"
        class="daily-performance__expand-button"
      >
        <v-icon>bhi-chevron-right</v-icon>
      </v-btn>
      <h4>{{ day.date | moment('DD MMM YYYY') }}</h4>
      <div class="daily-performance__states-bar--wrapper">
        <p>
          <span v-if="data.superCompleted" class="super-completed"
            ><i /><strong>{{ data.superCompleted }}</strong> Super
            Completed</span
          >
          <span v-if="data.completed" class="completed"
            ><i /><strong>{{ data.completed }}</strong> Completed</span
          >
          <span v-if="data.partially" class="partially"
            ><i /><strong>{{ data.partially }}</strong> Attempted</span
          >
          <span v-if="data.skipped" class="skipped"
            ><i /><strong>{{ data.skipped }}</strong> Skipped</span
          >
        </p>
        <horizontal-states-bar
          class="daily-performance__states-bar"
          :super-completed="data.superCompleted"
          :completed="data.completed"
          :skipped="data.skipped"
          :partially="data.partially"
        />
      </div>
      <v-spacer />
      <rating-stars v-if="!expanded" :rating="day.dayRating" />
    </div>
    <v-expand-transition>
      <div class="daily-performance__row--bottom mt-6" v-show="expanded">
        <v-row>
          <v-col cols="3">
            <h5 class="mb-3 mt-3">Day rating:</h5>
            <rating-stars :rating="day.dayRating" class="mb-4" />
            <p>{{ day.dayRatingComment }}</p>
            <h5 class="mb-3">Metrics:</h5>
            <horizontal-value-bar
              v-if="data.time"
              :value="data.progressTime"
              :total="data.time"
              type="time"
            />
            <div class="alternative-details" v-else>
              <v-icon class="mr-2">bhi-clock2</v-icon>
              <p class="mb-0">{{ data.progressTime }} min</p>
            </div>
            <horizontal-value-bar
              v-if="data.weight"
              :value="data.progressWeight"
              :total="data.weight"
              type="weight"
            />
            <div class="alternative-details" v-else>
              <v-icon class="mr-2">bhi-exercise</v-icon>
              <p class="mb-0">{{ data.progressWeight }} lbs</p>
            </div>
            <horizontal-value-bar
              v-if="data.distance"
              :value="data.progressDistance"
              :total="data.distance"
              type="distance"
            />
            <div class="alternative-details" v-else>
              <v-icon class="mr-2">bhi-activity</v-icon>
              <p class="mb-0">{{ data.progressDistance }} m</p>
            </div>
            <v-tooltip left open-delay="200">
              <template v-slot:activator="{ on, attrs }">
                <div class="alternative-details" v-bind="attrs" v-on="on">
                  <v-icon class="mr-2">bhi-walk</v-icon>
                  <p class="mb-0">{{ data.steps }}</p>
                </div>
              </template>
              Steps
            </v-tooltip>
          </v-col>
          <v-col cols="9">
            <div>
              <h5 class="mb-3 mt-3">
                {{
                  tenantTexts.participantRoutinesProgress
                    .sectionDailyPerformance.exercisesAndActivities
                }}
              </h5>
              <v-btn-toggle
                v-model="modeType"
                borderless
                color="accent"
                mandatory
                small
                v-if="computedDayReport"
              >
                <v-btn value="all"
                  >All ({{ computedDayReport.counts.allCount }})</v-btn
                >
                <v-btn value="exercises"
                  >Exercises ({{
                    computedDayReport.counts.exercisesCount
                  }})</v-btn
                >
                <v-btn value="activitiesAM"
                  >{{
                    tenantTexts.participantRoutinesProgress
                      .sectionDailyPerformance.AM_Activities
                  }}
                  ({{ computedDayReport.counts.activitiesAMCount }})</v-btn
                >
                <v-btn value="activitiesPM"
                  >{{
                    tenantTexts.participantRoutinesProgress
                      .sectionDailyPerformance.PM_Activities
                  }}
                  ({{ computedDayReport.counts.activitiesPMCount }})</v-btn
                >
                <v-btn value="skipped"
                  >Skipped ({{ computedDayReport.counts.skippedCount }})</v-btn
                >
              </v-btn-toggle>
              <div
                class="daily-performance__tile--wrapper"
                v-if="computedDayReport"
              >
                <exercise-report-tile
                  v-for="item in computedDayReport.items"
                  :key="item.id"
                  :type="item.type"
                  :mode="modeType"
                  :calendar-id="item.id"
                  :exercise="item.exercise"
                  :activity="item.activity"
                  :show-outline="modeType === 'all'"
                />
              </div>
            </div>
          </v-col>
        </v-row>
      </div>
    </v-expand-transition>
  </div>
</template>

<script>
import HorizontalStatesBar from '@/components/exercises/HorizontalStatesBar.vue'
import HorizontalValueBar from '@/components/exercises/HorizontalValueBar.vue'
import RatingStars from '@/components/exercises/RatingStars.vue'
import ExerciseReportTile from '@/components/exercises/ExerciseReportTile.vue'
import TenantTextsMixin from '@/mixins/tenantTexts'

export default {
  name: 'DailyPerformanceRow',
  components: {
    HorizontalStatesBar,
    HorizontalValueBar,
    RatingStars,
    ExerciseReportTile,
  },
  mixins: [TenantTextsMixin],
  props: {
    day: {
      type: Object,
      default: () => {},
      required: true,
    },
    data: {
      type: Object,
      default: () => {},
    },
    dayReport: {
      type: [Boolean, Object],
      default: false,
    },
    stateOpen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expanded: false,
      modeType: 'all',
    }
  },
  watch: {
    stateOpen(newValue, oldValue) {
      if (newValue != oldValue) {
        if (newValue) this.expanded = true
        else this.expanded = false
      }
      if (newValue == false) {
        this.expanded = false
      }
    },
    $route: {
      handler() {
        this.expanded = false
      },
      deep: true,
    },
  },
  computed: {
    computedDayReport() {
      let report = this.dayReport
      if (this.dayReport) {
        report.counts = {
          allCount: this.dayReport.items.length,
          exercisesCount: this.dayReport.items.filter(item => item.exercise)
            .length,
          activitiesAMCount: this.dayReport.items.filter(
            item => item.activity && item.activity.time != 'EVENING',
          ).length,
          activitiesPMCount: this.dayReport.items.filter(
            item => item.activity && item.activity.time == 'EVENING',
          ).length,
          skippedCount: this.dayReport.items.filter(
            item => item.exercise?.skipped || item.activity?.skipped,
          ).length,
        }
      }
      return report
    },
  },
  methods: {
    expand() {
      this.expanded = !this.expanded
      if (this.expanded) this.$emit('load-day-report', this.day.date)
    },
  },
}
</script>
<style lang="scss" scoped>
.daily-performance {
  &__row {
    h4 {
      color: $bh-blue;
      margin-bottom: 0;
      width: 120px;
      flex-shrink: 0;
    }
    &--top {
      display: flex;
      align-items: center;
    }
    &--bottom {
      h5 {
        color: $bh-black;
      }
      .alternative-details {
        color: $bh-gray;
        margin-bottom: 16px;
        display: flex;
        align-items: center;
      }
    }
    &.expanded {
      .daily-performance__expand-button {
        .v-icon {
          transform: rotate(90deg);
        }
      }
    }
  }
  &__states-bar {
    margin-bottom: 8px;
    &--wrapper {
      margin-left: 30px;
      width: 700px;
      p {
        margin-bottom: 4px;
      }
      span {
        display: inline-block;
        margin-right: 24px;
        &.super-completed {
          i {
            background: #945eff;
          }
        }
        &.completed {
          i {
            background: #00a0e0;
          }
        }
        &.partially {
          i {
            background: #bdd0ff;
          }
        }
        &.skipped {
          color: #ff6760;
          i {
            background: #ff6760;
          }
        }
      }
      i {
        width: 8px;
        height: 8px;
        display: inline-block;
        border-radius: 4px;
        margin-right: 8px;
        vertical-align: middle;
      }
    }
  }
  &__tile--wrapper {
    display: flex;
    flex-wrap: wrap;
  }
}
</style>
